/**
 * recordEligibility — the fast-side phantom floor for RECORDS.
 *
 * Problem (2026-09-01): the Scores "Fastest length" tile showed a 9.4 s
 * breaststroke, and set bests showed a 14.8 s butterfly / 13.6 s freestyle
 * over 25 m. Those are watch glitches — a wall touch split one length into
 * two events — and physically impossible for this swimmer (typical breast 25
 * ≈ 37 s, fly ≈ 28 s). The existing phantom pass in exclusions.ts only
 * catches the sub-13 s half; the other half of a split length is 14–17 s and
 * slips into every "fastest" tile.
 *
 * Owner rules ("everything counts; 3 buckets not exclusion; never auto-exclude
 * slow drills"): a flagged length is NOT excluded from the swim. It still
 * counts as a length, still sits in every average and set time, still draws
 * on the ribbon. It just cannot set a RECORD. This module answers one
 * question only: "could this length be a personal best?"
 *
 * Rule — a lap is record-eligible when ALL of:
 *   1. it is not effectiveExcluded;
 *   2. its duration ≥ 0.6 × the swimmer's own median duration for that stroke
 *      over ALL history (per 25 m, scaled by lengthMeters / 25). With fewer
 *      than 8 laps of that stroke on record, a hard physical floor per 25 m
 *      stands in: fly 11 s, back 11.5 s, breast 13 s, free 10 s;
 *   3. its stroke count is null (no sample) or ≥ 3.
 *
 * The median comes from the swimFacts cache — each swim stores (n, median)
 * per stroke and this module takes the lap-count-weighted median across
 * swims — so no consumer ever needs a second pass over raw laps. At
 * extraction time the swim's OWN laps are folded in (medianWithLaps) so even
 * the first swim ever scanned has a real median to measure against.
 *
 * Calibration on the fixture history (2026-09-01): 2.6 % of counted lengths
 * flagged; the fastest surviving lengths are fly 17.2 s, back 19.5 s, breast
 * 22.4 s, free 16.9 s. See recordEligibility.test.ts for the real-data checks.
 */
import type { Lap } from "./laps";

/** Per-stroke median duration per 25 m and the lap count behind it. */
export interface StrokeMedian {
  medianS: number;
  n: number;
}
export type MedianByStroke = Record<string, StrokeMedian>;

/** Structural subset of Lap the rule reads. strokeCount / lengthMeters are
 *  optional so the older lap-only test fixtures still type-check. */
export type EligibilityLap = Pick<Lap, "durationSeconds" | "strokeStyle" | "effectiveExcluded"> &
  Partial<Pick<Lap, "strokeCount" | "lengthMeters">>;

/** Structural subset of SwimFactsEntry the median reads (keeps this module
 *  import-free of allTimeAggregates, which imports it). */
export interface MedianSource {
  poolLengthMeters: number | null;
  strokes: Record<string, { n: number; medianS: number }> | null;
}

export const RECORD_FLOOR_RATIO = 0.6;
/** Below this many laps of a stroke the median is noise; use the hard floor. */
export const MEDIAN_MIN_LAPS = 8;
export const MIN_RECORD_STROKES = 3;
/** Hard physical floor per 25 m when the swimmer has no history for a stroke.
 *  An unlisted stroke (Kickboard, Mixed, Unknown) takes the lowest of these. */
export const HARD_FLOOR_PER_25: Record<string, number> = {
  Butterfly: 11,
  Backstroke: 11.5,
  Breaststroke: 13,
  Freestyle: 10,
};
const DEFAULT_HARD_FLOOR_PER_25 = HARD_FLOOR_PER_25.Freestyle;

/** Lower weighted median; null for an empty list. */
export function weightedMedian(items: { v: number; w: number }[]): number | null {
  const sorted = items.filter((i) => i.w > 0).sort((a, b) => a.v - b.v);
  if (sorted.length === 0) return null;
  const total = sorted.reduce((s, i) => s + i.w, 0);
  let acc = 0;
  for (const i of sorted) {
    acc += i.w;
    if (acc >= total / 2) return i.v;
  }
  return sorted[sorted.length - 1].v;
}

/** The slowest a length of `stroke` in a `lengthMeters` pool may be and still count as a record. */
export function recordFloorSeconds(
  stroke: string,
  lengthMeters: number,
  medianByStroke: MedianByStroke,
): number {
  const scale = lengthMeters / 25;
  const m = medianByStroke[stroke];
  if (m && m.n >= MEDIAN_MIN_LAPS) return RECORD_FLOOR_RATIO * m.medianS * scale;
  return (HARD_FLOOR_PER_25[stroke] ?? DEFAULT_HARD_FLOOR_PER_25) * scale;
}

/** Could this length be a personal best? (See module doc for the rule.) */
export function isRecordEligible(lap: EligibilityLap, medianByStroke: MedianByStroke): boolean {
  if (lap.effectiveExcluded) return false;
  if (!(lap.durationSeconds > 0)) return false;
  const strokeCount = lap.strokeCount ?? null;
  if (strokeCount !== null && strokeCount < MIN_RECORD_STROKES) return false;
  const lengthMeters = lap.lengthMeters ?? 25;
  return lap.durationSeconds >= recordFloorSeconds(lap.strokeStyle, lengthMeters, medianByStroke);
}

/**
 * The swimmer's median duration per stroke across the cache: each swim
 * contributes its own per-stroke median weighted by that stroke's lap count.
 * 25 m pool swims only (a 20 m length is not a 25; open water has no
 * lengths); carry-overs with `strokes: null` are skipped.
 */
export function medianDurationByStroke(entries: MedianSource[]): MedianByStroke {
  const items = new Map<string, { v: number; w: number }[]>();
  for (const e of entries) {
    if (e.poolLengthMeters !== 25 || e.strokes === null) continue;
    for (const [stroke, s] of Object.entries(e.strokes)) {
      if (!(s.n > 0)) continue;
      const list = items.get(stroke) ?? [];
      list.push({ v: s.medianS, w: s.n });
      items.set(stroke, list);
    }
  }
  const out: MedianByStroke = {};
  for (const [stroke, list] of items) {
    const medianS = weightedMedian(list);
    if (medianS === null) continue;
    out[stroke] = { medianS, n: list.reduce((s, i) => s + i.w, 0) };
  }
  return out;
}

/**
 * Fold one swim's own counted laps into a prior (the cache's medians) so the
 * floor used while extracting THAT swim already knows about it. Durations are
 * normalized to 25 m. Pure — returns a new map.
 */
export function medianWithLaps(prior: MedianByStroke, laps: EligibilityLap[]): MedianByStroke {
  const own = new Map<string, { v: number; w: number }[]>();
  for (const l of laps) {
    if (l.effectiveExcluded || !(l.durationSeconds > 0)) continue;
    const len = l.lengthMeters ?? 25;
    const list = own.get(l.strokeStyle) ?? [];
    list.push({ v: (l.durationSeconds * 25) / len, w: 1 });
    own.set(l.strokeStyle, list);
  }
  const out: MedianByStroke = { ...prior };
  for (const [stroke, list] of own) {
    const p = prior[stroke];
    const items = p ? [{ v: p.medianS, w: p.n }, ...list] : list;
    out[stroke] = { medianS: weightedMedian(items)!, n: (p?.n ?? 0) + list.length };
  }
  return out;
}
